// The draft an administrator holds while editing the runtime parameters on
// Configurari, and the three questions the sticky footer asks of it: is anything
// changed, is anything out of bounds, and what exactly gets sent.
//
// Split out of SystemConfigPage.tsx so the rules are testable without a DOM.
// They are not decoration:
//
//  - A key the API did not declare, and a key it declared as NOT editable, can
//    never reach the request body. The five startup parameters are refused by
//    the enforcing service anyway (spec 002 FR-030), but a screen that asks for
//    something it was told it may not have is the same class of defect this
//    feature exists to remove — a control acting outside what its label governs.
//  - A value outside the bounds the API declared never reaches the request
//    body either. The bounds are the owning service's Joi schema, and a value
//    the screen accepts but the service rejects at its next boot is caiet
//    AVAIL-03 territory (docs/shape-ops-system-config.md §3.5).
//  - The payload carries the API's OWN key names, taken from the response, not
//    a second list declared here. That is what keeps the unit contract at the
//    bff edge, where §3.5 puts it: the screen reads and writes minutes and
//    months, and the bff converts to the seconds and months the stores hold.

/** The units the bff declares for a scalar setting (`service-settings.ts`). */
export type SettingUnit = 'minutes' | 'seconds' | 'days' | 'months';

export interface ScalarSetting {
  key: string;
  unit: SettingUnit;
  /**
   * What the owning service enforces right now, or null when that service did
   * not answer. Null is not a zero and not a boot default standing in for one:
   * the screen shows what it knows and says the rest is unknown.
   */
  value: number | null;
  min: number;
  max: number;
  /** True once the owning service keeps this parameter as data. */
  editable: boolean;
  /** False when the owning service could not be reached on the last read. */
  available: boolean;
}

/** Keyed by `ScalarSetting.key`; holds only what the administrator has touched. */
export type SettingsDraft = Readonly<Record<string, number>>;

export interface PendingChanges {
  /** Editable, changed and in-bounds, under the API's own key names. */
  payload: Record<string, number>;
  /** At least one editable value differs from what the service reports. */
  dirty: boolean;
  /** At least one changed value is outside the bounds the API declared. */
  invalid: boolean;
}

/** The value to show in the field: the administrator's if they typed one. */
export function draftedValue(setting: ScalarSetting, draft: SettingsDraft): number | null {
  const typed = draft[setting.key];
  return typed === undefined ? setting.value : typed;
}

/**
 * Which of the three rows this parameter gets. The distinction is the whole
 * point of the screen, so it is one decision in one place rather than three
 * conditions spread through the markup:
 *
 *  - `editable` — the owning service keeps it and reported its value: a field.
 *  - `unreadable` — that service did not answer. The value is unknown, and a
 *    boot default rendered in its place would be the screen claiming to know the
 *    effective configuration. Writing over a number nobody read is also how an
 *    administrator sets a parameter to something they never saw.
 *  - `read-only` — the value is known but SIMSM does not keep it, so a field
 *    would be a control that saves nothing.
 */
export type SettingRowKind = 'editable' | 'unreadable' | 'read-only';

export function settingRowKind(setting: ScalarSetting): SettingRowKind {
  if (!setting.available || setting.value === null) return 'unreadable';
  return setting.editable ? 'editable' : 'read-only';
}

export function isOutOfRange(setting: ScalarSetting, value: number): boolean {
  return !Number.isInteger(value) || value < setting.min || value > setting.max;
}

export function pendingChanges(
  settings: readonly ScalarSetting[],
  draft: SettingsDraft,
): PendingChanges {
  const payload: Record<string, number> = {};
  let dirty = false;
  let invalid = false;

  for (const setting of settings) {
    // Not editable, or read from a service that did not answer — the row is
    // read-only, so a draft entry for it could only come from a stale draft the
    // API has since narrowed. Drop it silently: the row re-renders read-only in
    // the same pass.
    if (settingRowKind(setting) !== 'editable') continue;
    const typed = draft[setting.key];
    if (typed === undefined || typed === setting.value) continue;
    dirty = true;
    if (isOutOfRange(setting, typed)) {
      invalid = true;
      continue;
    }
    payload[setting.key] = typed;
  }

  return { payload, dirty, invalid };
}

/** What the footer says about the draft, in one word the caller turns into copy. */
export function draftStatus(changes: PendingChanges): 'clean' | 'dirty' | 'invalid' {
  if (changes.invalid) return 'invalid';
  return changes.dirty ? 'dirty' : 'clean';
}
